import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar/Sidebar';
import Navbar from './components/Navbar/Navbar';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearError, clearMessage } from './rtk/slices/authSlice';
import { getProfile } from './rtk/thunks/authThunk';
import { fetchDashboardData } from './rtk/thunks/dashBoardThunk';
import Dashboard from './pages/Dashboard/Dashboard';
import CoursesModule from './pages/CoursesModule/CoursesModule';
import StudyPlanner from './pages/StudyPlanner/StudyPlanner';
import QuestionBank from './pages/QuestionBank/QuestionBank';
import StudyVault from './pages/StudyVault/StudyVault';
import AccountSettings from './pages/AccountSettings/AccountSettings';
import LandingPage from './pages/LandingPage/LandingPage';
import SignIn from './pages/Auth/SignIn';
import SignUp from './pages/Auth/SignUp';
import VerifyEmail from './pages/Auth/verifyEmail';
import ChangePassword from './pages/Auth/changePassword';
import ForgotPassword from './pages/Auth/forgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import Onboarding from './pages/Auth/Onboarding';
import Terms from './pages/Terms/Terms';
import Privacy from './pages/Privacy/Privacy';
import Contact from './pages/Contact/Contact';
import TopButton from './components/topButton/topButton';
import WhatsAppButton from './components/WhatsAppButton/WhatsAppButton';
import BackButton from './components/backButton/backButton';
import './App.css';
import UserProfile from './pages/userProfile/userProfile';
import PaymentSuccess from './pages/Payment/PaymentSuccess';
import PaymentCancel from './pages/Payment/PaymentCancel';
import QuickQuiz from './pages/QuestionBank/quickQuiz';
import PdfViewer from './pages/PdfViewer/PdfViewer';
import TitleUpdater from './components/titleUpdater/titleUpdater';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import LearningHub from './pages/LearningHub/LearningHub';
import LearningHubArticle from './pages/LearningHub/LearningHubArticle';
import { ArticlesIndexRedirect, ArticlesSlugRedirect } from './pages/LearningHub/ArticlesRedirect';
// The Layout component now manages the sidebar state and overall structure
function Layout({ children }) {
  const location = useLocation();
  
  // --- START OF CHANGE ---
  // Define an array of paths that should NOT have the main layout
  const noLayoutPaths = ['/', '/sign-in','/sign-up', '/verify-email', '/forgot-password', '/reset-password', '/onboarding', '/payment/success', '/payment/cancel', '/pdf-viewer', '/contact', '/articles', '/learning-hub', '/terms', '/privacy'];
  
  const shouldHideLayout =
    noLayoutPaths.includes(location.pathname) ||
    location.pathname.startsWith('/articles/') ||
    location.pathname.startsWith('/learning-hub/');
  // --- END OF CHANGE ---

  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const { user, access_token } = useSelector((state) => state.auth);
  const storedAccessToken = localStorage.getItem('access_token');
  const userName =
    user?.first_name ||
    user?.name ||
    user?.full_name ||
    user?.username ||
    user?.email?.split('@')[0] ||
    'User';

  // clear transient auth messages/errors when the route changes
  const dispatch = useDispatch();
  const dashboardStats = useSelector((state) => state.dashboard?.stats);
  useEffect(() => {
    dispatch(clearError());
    dispatch(clearMessage());
  }, [location.pathname, dispatch]);

  useEffect(() => {
    if (!user && (access_token || storedAccessToken)) {
      dispatch(getProfile());
    }
  }, [access_token, dispatch, storedAccessToken, user]);

  useEffect(() => {
    if (shouldHideLayout || dashboardStats) return;
    if (!(access_token || storedAccessToken)) return;
    dispatch(fetchDashboardData());
  }, [access_token, dashboardStats, dispatch, shouldHideLayout, storedAccessToken]);

  // If the current path should have no layout, just render the page component
  if (shouldHideLayout) {
    return <>{children}</>;
  }

  // Otherwise, render the page within the main app layout
  return (
    <div className={`app-container ${isSidebarOpen ? 'sidebar-is-open' : ''}`}>
      <Sidebar 
        isOpen={isSidebarOpen} 
        setOpen={setSidebarOpen} 
      />
      <div className="main-content-wrapper">
        <Navbar userName={userName} />
        <main className="page-content">{children}</main>
      </div>
    </div>
  );
}

// No changes are needed in the App function below
function App() {
  return (
    <Router>
      <Layout>
          <TitleUpdater/>
        <Routes>
          {/* Public routes - reachable without a login session */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/learning-hub" element={<LearningHub />} />
          <Route path="/learning-hub/:slug" element={<LearningHubArticle />} />
          <Route path="/articles" element={<ArticlesIndexRedirect />} />
          <Route path="/articles/:slug" element={<ArticlesSlugRedirect />} />

          {/* Protected routes - require an active session */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/resources" element={<ProtectedRoute><CoursesModule /></ProtectedRoute>} />
          <Route path="/study-planner" element={<ProtectedRoute><StudyPlanner /></ProtectedRoute>} />
          <Route path="/question-bank" element={<ProtectedRoute><QuestionBank /></ProtectedRoute>} />
          <Route path="/study-vault" element={<ProtectedRoute><StudyVault /></ProtectedRoute>} />
          <Route path="/account-settings" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="/user-profile" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
          <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
          <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
          <Route path='/quick-quiz' element={<ProtectedRoute><QuickQuiz /></ProtectedRoute>} />
          <Route path="/pdf-viewer" element={<ProtectedRoute><PdfViewer /></ProtectedRoute>} />
          <Route path="*" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        </Routes>
      </Layout>
      <BackButton />
      <WhatsAppButton />
      <TopButton />
    </Router>
  );
}

export default App;
